/**
 * Tiny platform helpers used to tailor microphone-permission help text.
 * UA sniffing is normally a smell, but the iOS permission path is genuinely
 * platform-specific and there's no feature detection for it.
 */

export function isIos(): boolean {
  if (typeof navigator === 'undefined' || typeof document === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as Mac; touch support gives it away.
  return ua.includes('Macintosh') && 'ontouchend' in document;
}

export function isStandalonePwa(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  return (window.navigator as { standalone?: boolean }).standalone === true;
}

export function isFirefox(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Firefox/i.test(navigator.userAgent);
}

/**
 * True in Brave (any platform). Brave exposes `navigator.brave.isBrave`
 * as its official detection API; we check for the function's existence
 * synchronously rather than awaiting the promise so error handlers can
 * branch without going async. Other Chromium browsers do not expose
 * `navigator.brave`.
 *
 * Why we care: Brave keeps the Web Speech API surface but strips out
 * the Google API keys that authorize transcription, so voice input fails
 * with a 'network' error instead of working — opposite of what the API's
 * presence would suggest.
 */
export function isBrave(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { brave?: { isBrave?: () => Promise<boolean> } };
  return typeof nav.brave?.isBrave === 'function';
}

/**
 * Suggests the most direct path the user can take to grant the mic
 * permission permanently, by platform.
 */
export function micPermissionHelpText(): string {
  if (isIos()) {
    return 'To make this permanent on iPhone or iPad: open the Settings app, then Apps, then Safari, then Microphone, and set aiprompt.si to Allow.';
  }
  if (isFirefox()) {
    return 'Firefox does not implement the Web Speech API. Use Chrome, Edge, Safari, or Opera for voice input.';
  }
  if (isBrave()) {
    return 'Brave blocks the Google speech service that the Web Speech API depends on, so voice input fails even with mic permission granted. Use Chrome, Edge, Safari, or Opera for voice, or type your request instead.';
  }
  return 'Click the small lock or camera icon in the address bar and set Microphone to Allow for aiprompt.si.';
}
